import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, XCircle, Landmark, FileSignature, Lock, TrendingUp } from "lucide-react";
import { api } from "../../lib/api";

/**
 * Client portal card for the recurring ACH authorization.
 * - When no authorization exists (or was revoked), shows a legal disclosure +
 *   type-your-name signature line + explicit consent checkbox.
 * - When active, shows the signed name, date, and a Revoke button.
 */
export default function AchAuthorizationCard({ bankAccounts = [] }) {
  const [state, setState] = useState(null); // { status, signed_name, authorized_at, terms_version, bank_last4 }
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [signedName, setSignedName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [chosenLast4, setChosenLast4] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/portal/ach-authorization")
      .then(({ data }) => setState(data))
      .catch(() => toast.error("Could not load authorization status."))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const sign = async () => {
    if (!signedName.trim() || signedName.trim().length < 2) {
      toast.error("Type your full name to sign.");
      return;
    }
    if (!agreed) {
      toast.error("Please check the authorization box.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/portal/ach-authorization", {
        signed_name: signedName.trim(),
        agreed: true,
        bank_last4: chosenLast4 || undefined,
      });
      setState(data);
      setShowForm(false);
      setSignedName("");
      setAgreed(false);
      toast.success("Recurring authorization signed — a copy was emailed to you.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save authorization.");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (locked) return;
    if (!window.confirm(
      "Cancel your recurring ACH authorization? We'll stop initiating drafts within 3 business days. " +
      "If you're still inside your minimum term, amounts owed for the remainder remain payable — " +
      "we'll reach out to arrange it.",
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/portal/ach-authorization/revoke");
      setState(data);
      toast.success("Authorization revoked. We've notified Kalib and John.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revoke right now.");
    } finally {
      setBusy(false);
    }
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }); }
    catch { return iso; }
  };

  const isActive = state?.status === "active";
  const isRevoked = state?.status === "revoked";
  const lock = state?.lock || {};
  const locked = isActive && lock.locked;

  return (
    <div
      data-testid="portal-ach-authorization"
      className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-6"
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] uppercase tracking-[0.24em] text-[#c9a961] flex items-center gap-1.5">
          <FileSignature className="h-3 w-3" /> Recurring payment authorization
        </span>
        {isActive && (
          <span
            data-testid="ach-auth-active-chip"
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] bg-[#6b7c3a]/12 text-[#4d5a24] px-2.5 py-1 rounded-full"
          >
            <ShieldCheck className="h-3 w-3" /> Active
          </span>
        )}
        {isRevoked && (
          <span
            data-testid="ach-auth-revoked-chip"
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] bg-[#b23a48]/10 text-[#b23a48] px-2.5 py-1 rounded-full"
          >
            <XCircle className="h-3 w-3" /> Revoked
          </span>
        )}
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-xs text-[#1e3d3a]/55">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading authorization…
        </div>
      )}

      {!loading && isActive && (
        <div className="space-y-3">
          <p className="text-sm text-[#1e3d3a] leading-relaxed">
            You authorized Elevare Solutions to debit your account on{" "}
            <strong>{fmtDate(state.authorized_at)}</strong>, signed as{" "}
            <strong>{state.signed_name}</strong>{state.bank_last4 ? <> from the account ending in <strong>••••{state.bank_last4}</strong></> : ""}.
          </p>
          <p className="text-xs text-[#1e3d3a]/65 leading-relaxed" data-testid="ach-auth-term-note">
            Your signed terms include a minimum service commitment of{" "}
            <strong>{state.minimum_term_months || 3} months</strong>
            {state.minimum_term_ends ? <> (through <strong>{fmtDate(state.minimum_term_ends)}</strong>)</> : ""}.
            After that, drafts continue on your agreed schedule until you cancel.
          </p>
          <p className="text-xs text-[#1e3d3a]/65 leading-relaxed">
            {locked ? (
              <>
                This authorization is locked for the first <strong>90 days</strong> after your initial payment —
                cancellation unlocks on <strong>{fmtDate(lock.lock_until)}</strong>
                {lock.days_remaining ? <> (in {lock.days_remaining} day{lock.days_remaining === 1 ? "" : "s"})</> : ""}.
                To make a change sooner, you can upgrade your plan anytime.
              </>
            ) : (
              <>
                You can cancel this authorization now. Cancellations take effect within 3 business days of receipt;
                amounts owed under the minimum term remain payable.
              </>
            )}{" "}
            Terms version on file: <span className="font-mono text-[10px]">{state.terms_version}</span>.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid="ach-auth-revoke"
              onClick={revoke}
              disabled={busy || locked}
              title={locked ? `Unlocks ${fmtDate(lock.lock_until)}` : undefined}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#b23a48]/40 text-[#b23a48] px-4 py-1.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#b23a48]/8 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors duration-300"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : locked ? <Lock className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
              {locked ? `Locked until ${fmtDate(lock.lock_until)}` : "Cancel authorization"}
            </button>
            {locked && (
              <a
                href="/#pricing"
                data-testid="ach-auth-upgrade"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-4 py-1.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300"
              >
                <TrendingUp className="h-3.5 w-3.5" /> Upgrade plan
              </a>
            )}
          </div>
        </div>
      )}

      {!loading && !isActive && (
        <div className="space-y-3">
          {isRevoked && (
            <div className="text-xs text-[#1e3d3a]/60 border-l-2 border-[#b23a48]/40 pl-3">
              You revoked your authorization on {fmtDate(state.revoked_at)}.
              You can re-sign below at any time.
            </div>
          )}
          <p className="text-sm text-[#1e3d3a]/75 leading-relaxed">
            To let Kalib and John run the recurring recovery-service drafts automatically,
            we'll need a signed authorization on file. You're signing on for a minimum of 3 months of
            service, and drafts continue until you cancel. It takes 30 seconds.
          </p>
          {!showForm && (
            <button
              type="button"
              data-testid="ach-auth-open"
              onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-4 py-2 text-[11px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300"
            >
              <FileSignature className="h-3.5 w-3.5" /> Review &amp; sign authorization
            </button>
          )}
          {showForm && (
            <div className="rounded-xl border border-[#c9a961]/30 bg-[#c9a961]/5 p-4 space-y-3" data-testid="ach-auth-form">
              <div className="text-xs text-[#1e3d3a] leading-relaxed max-h-56 overflow-y-auto pr-2 border-l-2 border-[#c9a961]/50 pl-3">
                <p className="font-semibold mb-2">Recurring ACH Debit Authorization</p>
                <p>
                  I authorize <strong>Elevare Solutions LLC</strong> (Elevare) to initiate electronic
                  debit entries from the bank account I have linked in this portal to pay for the
                  services I have contracted with Elevare, on the schedule and in the amounts agreed to
                  in my service agreement.
                </p>
                <p className="mt-2">
                  <strong>Minimum service term.</strong> I agree to a minimum commitment of{" "}
                  <strong>three (3) months</strong> of service from the date I sign this authorization.
                  After the initial three-month term, my authorization continues and Elevare may keep
                  drafting my account on the agreed schedule <strong>until I cancel it</strong>.
                </p>
                <p className="mt-2">
                  I understand that this authorization will remain in full force and effect until I
                  cancel it in writing (via my portal, or by emailing kalib@elevaresolutions.me), that
                  Elevare requires at least three (3) business days' notice to process a cancellation, and
                  that amounts owed for the remainder of the minimum three-month term remain payable even
                  if I cancel before that term ends.
                </p>
                <p className="mt-2">
                  I understand that if a debit is returned for any reason (insufficient funds, closed account, etc.),
                  I may be liable for the returned amount plus any bank fees, and Elevare may re-attempt the debit up
                  to two additional times as permitted by NACHA rules.
                </p>
                <p className="mt-2">
                  I represent that I am an owner or authorized signer on the linked account. I confirm the account
                  information provided is accurate, and I authorize Elevare's bank and processor to accept these debits.
                </p>
              </div>

              {bankAccounts.length > 0 && (
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/60 mb-1 block">
                    Draft from (optional)
                  </span>
                  <select
                    data-testid="ach-auth-account"
                    value={chosenLast4}
                    onChange={(e) => setChosenLast4(e.target.value)}
                    className="w-full text-xs rounded border border-[#1e3d3a]/15 bg-white px-2 py-1.5 focus:border-[#c9a961] focus:outline-none"
                  >
                    <option value="">— any linked account —</option>
                    {bankAccounts.map((b) => (
                      <option key={b.payment_method_id || b.fc_account_id || b.last4} value={b.last4}>
                        {b.brand || "Bank"} ••••{b.last4}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/60 mb-1 block">
                  Type your full legal name to sign
                </span>
                <input
                  data-testid="ach-auth-name"
                  value={signedName}
                  onChange={(e) => setSignedName(e.target.value)}
                  className="w-full font-head text-lg text-[#1e3d3a] rounded border-b-2 border-[#1e3d3a]/30 bg-transparent px-1 py-1 focus:border-[#c9a961] focus:outline-none"
                  placeholder="e.g. Jane Ridgeline"
                />
              </label>

              <label className="flex items-start gap-2 text-xs text-[#1e3d3a]/80 leading-relaxed">
                <input
                  type="checkbox"
                  data-testid="ach-auth-agree"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-0.5 h-3.5 w-3.5 accent-[#1e3d3a]"
                />
                <span>
                  I have read and agree to the Recurring ACH Debit Authorization above, including the
                  <strong> minimum three (3) month service term</strong>, and I understand Elevare will
                  continue drafting my account until I cancel this authorization.
                </span>
              </label>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-testid="ach-auth-sign"
                  onClick={sign}
                  disabled={busy || !signedName.trim() || !agreed}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-4 py-2 text-[11px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300"
                >
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                  Authorize recurring debits
                </button>
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setAgreed(false); setSignedName(""); }}
                  className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 hover:text-[#1e3d3a] px-3 py-2 transition-colors duration-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
